/**
 * ToggleButtonGroup - Generic multi-button toggle component
 *
 * Pill-shaped toggle buttons for mode selection, flags, etc.
 * Used by Order Ticket (TIF flags, Release), Alert Editor (Simple/Advanced).
 *
 * @example
 * ```tsx
 * const TIF_OPTIONS = [
 *   { value: 'Day', label: 'DAY' },
 *   { value: 'GTC', label: 'GTC' },
 *   { value: 'GTD', label: 'GTD' },
 * ]
 *
 * <ToggleButtonGroup
 *   value={timeInForce}
 *   options={TIF_OPTIONS}
 *   onChange={setTimeInForce}
 * />
 * ```
 */

import { cn } from '../../utils'

export interface ToggleOption<T extends string = string> {
  value: T
  label: string
}

export interface ToggleButtonGroupProps<T extends string = string> {
  /** Currently selected value */
  value: T
  /** Available options */
  options: ToggleOption<T>[]
  /** Callback when selection changes */
  onChange: (value: T) => void
  /** Additional className for the container */
  className?: string
}

/**
 * ToggleButtonGroup
 *
 * Generic multi-button toggle with pill shape.
 * Active button shows primary color, inactive shows card background.
 */
export function ToggleButtonGroup<T extends string = string>({
  value,
  options,
  onChange,
  className,
}: ToggleButtonGroupProps<T>) {
  return (
    <div className={cn('flex rounded overflow-hidden border border-border w-fit', className)}>
      {options.map((option, idx) => {
        const isActive = value === option.value

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={cn(
              'px-2.5 py-1 text-xs font-medium transition-colors',
              isActive
                ? 'bg-primary text-primary-foreground'
                : 'bg-card text-foreground hover:bg-accent',
              idx > 0 && 'border-l border-border'
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
